import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
} from "@mui/material";
import { patchCcRechazo } from "./ApiServices";
import ExpedienteArchivos from "./ExpedienteArchivos";
import {
  TIPOS_ARCHIVO_RECHAZO,
  esReposicionExenta,
} from "./chequesRechazos.constants";

export default function ModalCcRechazo({ open, onClose, rechazo, onOk }) {
  const [reposicionCliente, setReposicionCliente] = useState(true);
  const [nroNdCliente, setNroNdCliente] = useState("");
  const [nroFacturaGastos, setNroFacturaGastos] = useState("");
  const [gastosGestion, setGastosGestion] = useState("");
  const [gastosBanco, setGastosBanco] = useState("");
  const [obs, setObs] = useState("");
  const [omitirFacturaGastos, setOmitirFacturaGastos] = useState(false);
  const [confirmadoSisven, setConfirmadoSisven] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const exenta = esReposicionExenta(rechazo);

  useEffect(() => {
    if (!open) return;
    setReposicionCliente(!esReposicionExenta(rechazo));
    setNroNdCliente("");
    setNroFacturaGastos("");
    setGastosGestion(
      rechazo?.gastos_gestion_admin != null
        ? String(rechazo.gastos_gestion_admin)
        : ""
    );
    setGastosBanco(
      rechazo?.gastos_banco != null ? String(rechazo.gastos_banco) : ""
    );
    setObs("");
    setOmitirFacturaGastos(false);
    setConfirmadoSisven(false);
    setError("");
  }, [open, rechazo]);

  const idRechazo = rechazo?.id ?? rechazo?.id_rechazo;

  const guardar = async () => {
    if (!idRechazo) return;
    if (!nroNdCliente.trim()) {
      setError("Indicá la referencia de la ND a cliente o el id de ajuste CtaCte.");
      return;
    }
    if (!omitirFacturaGastos && !nroFacturaGastos.trim()) {
      setError("Indicá la factura de gastos o marcá omitir factura.");
      return;
    }
    if (!confirmadoSisven) {
      setError("Confirmá la carga manual en Sisven (ND y factura de gastos).");
      return;
    }
    setGuardando(true);
    setError("");
    try {
      const body = {
        nro_nd_cliente_ref: nroNdCliente.trim(),
        aviso_cierre: true,
        sisven_confirmado: true,
        reposicion_cliente: exenta ? false : reposicionCliente,
        obs: obs.trim() || undefined,
      };
      const nNd = Number(nroNdCliente);
      if (Number.isFinite(nNd) && nNd > 0) body.id_ajuste_ctacte = nNd;
      if (omitirFacturaGastos) {
        body.omitir_factura_gastos = true;
      } else {
        body.nro_factura_gastos_ref = nroFacturaGastos.trim();
        const nFac = Number(nroFacturaGastos);
        if (Number.isFinite(nFac) && nFac > 0) body.id_factura_gastos = nFac;
      }
      const gAdmin = Number(gastosGestion);
      if (gastosGestion.trim() !== "" && Number.isFinite(gAdmin)) {
        body.gastos_gestion_admin = gAdmin;
      }
      const gBanco = Number(gastosBanco);
      if (gastosBanco.trim() !== "" && Number.isFinite(gBanco)) {
        body.gastos_banco = gBanco;
      }
      await patchCcRechazo(idRechazo, body);
      onOk?.();
      onClose?.();
    } catch (err) {
      setError(err.message || "Error al cerrar el rechazo");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={guardando ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Cuentas Corrientes — cierre del rechazo</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {exenta ? (
            <Alert severity="warning">
              Reposición al proveedor no aplica (exención 30 días / no propio).
              Igual emití ND al cliente si corresponde generar saldo.
            </Alert>
          ) : (
            <Alert severity="info">
              Gestioná la reposición si aplica. Registrá ND a cliente, factura
              de gastos (banco o trasladados) y gastos de gestión administrativa.
              Sisven es carga manual.
            </Alert>
          )}
          {!exenta ? (
            <FormControlLabel
              control={
                <Checkbox
                  checked={reposicionCliente}
                  onChange={(e) => setReposicionCliente(e.target.checked)}
                  disabled={guardando}
                />
              }
              label="Gestioné la reposición del valor"
            />
          ) : null}
          <TextField
            size="small"
            fullWidth
            required
            label="Ref. ND cliente / id ajuste CtaCte"
            value={nroNdCliente}
            onChange={(e) => setNroNdCliente(e.target.value)}
            disabled={guardando}
            helperText="Nro. de ND a cliente o id de ajuste en cuenta corriente"
          />
          <TextField
            size="small"
            fullWidth
            required={!omitirFacturaGastos}
            label="Ref. factura de gastos / id factura"
            value={nroFacturaGastos}
            onChange={(e) => setNroFacturaGastos(e.target.value)}
            disabled={guardando || omitirFacturaGastos}
            helperText={
              omitirFacturaGastos
                ? "Factura omitida"
                : "Obligatorio, salvo que omitas la factura"
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={omitirFacturaGastos}
                onChange={(e) => {
                  const omitir = e.target.checked;
                  setOmitirFacturaGastos(omitir);
                  if (omitir) setNroFacturaGastos("");
                }}
                disabled={guardando}
              />
            }
            label="Omitir factura de gastos"
          />
          <TextField
            size="small"
            fullWidth
            label="Gastos banco / trasladados (opcional)"
            value={gastosBanco}
            onChange={(e) => setGastosBanco(e.target.value)}
            disabled={guardando}
          />
          <TextField
            size="small"
            fullWidth
            label="Gastos gestión administrativa (opcional)"
            value={gastosGestion}
            onChange={(e) => setGastosGestion(e.target.value)}
            disabled={guardando}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={confirmadoSisven}
                onChange={(e) => setConfirmadoSisven(e.target.checked)}
                disabled={guardando}
              />
            }
            label="Confirmé ND y factura de gastos en Sisven (paso manual)"
          />
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            label="Observaciones (opcional)"
            value={obs}
            onChange={(e) => setObs(e.target.value)}
            disabled={guardando}
          />
          <ExpedienteArchivos
            idRechazo={idRechazo}
            tipoDefault={TIPOS_ARCHIVO_RECHAZO.ND_CLIENTE}
            compact
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={guardando}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={guardar}
          disabled={guardando}
          startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Cerrar caso
        </Button>
      </DialogActions>
    </Dialog>
  );
}
